import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, ExternalLink, Save, X, Eye } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useSite } from "../../contexts/SiteContext";

const EMPTY = { slug: "", title: "", subtitle: "", body: "", seo_title: "", seo_description: "", published: true };

export default function AdminPages() {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const { refresh } = useSite();

    const fetchAll = () => {
        setLoading(true);
        api.get("/admin/pages")
            .then(({ data }) => setPages(data.pages))
            .finally(() => setLoading(false));
    };

    useEffect(fetchAll, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this page?")) return;
        try {
            await api.delete(`/admin/pages/${id}`);
            toast.success("Deleted");
            fetchAll();
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-pages">
            <div className="flex items-end justify-between">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Atelier</p>
                    <h1 className="mt-2 font-display text-4xl text-white">Pages</h1>
                    <p className="mt-1 text-sm text-white/50">
                        Rich-text pages — about, shipping, FAQ, privacy. Live at <code className="text-gold">/p/&lt;slug&gt;</code>.
                    </p>
                </div>
                <button
                    onClick={() => setEditing({ ...EMPTY })}
                    data-testid="admin-add-page"
                    className="inline-flex items-center gap-2 bg-gold px-5 py-3 text-[11px] uppercase tracking-[0.25em] text-obsidian hover:bg-gold-light"
                >
                    <Plus className="h-3 w-3" /> New page
                </button>
            </div>

            <div className="mt-8 space-y-3">
                {loading ? (
                    <p className="text-white/40">Loading…</p>
                ) : pages.length === 0 ? (
                    <p className="py-10 text-center text-white/40">No pages yet.</p>
                ) : (
                    pages.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`page-row-${p.id}`}
                            className="flex flex-col gap-4 border border-border-dim bg-surface p-5 md:flex-row md:items-center"
                        >
                            <div className="flex-1">
                                <p className="font-display text-xl text-white">{p.title}</p>
                                <p className="text-xs uppercase tracking-widest text-white/40">
                                    /p/{p.slug} {p.published ? "" : "· DRAFT"}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Link
                                    to={`/p/${p.slug}`}
                                    target="_blank"
                                    className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/70 hover:border-gold hover:text-gold"
                                    aria-label="View"
                                >
                                    <ExternalLink className="h-3 w-3" />
                                </Link>
                                <button
                                    onClick={() => setEditing(p)}
                                    data-testid={`page-edit-${p.id}`}
                                    className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/70 hover:border-gold hover:text-gold"
                                >
                                    <Edit2 className="h-3 w-3" />
                                </button>
                                <button
                                    onClick={() => remove(p.id)}
                                    data-testid={`page-delete-${p.id}`}
                                    className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/70 hover:border-heart hover:text-heart"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {editing && (
                <PageEditor
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        fetchAll();
                        refresh();
                    }}
                />
            )}
        </div>
    );
}

function PageEditor({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const isEdit = !!initial.id;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.title || !form.slug) return toast.error("Title and slug are required");
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/admin/pages/${initial.id}`, form);
            } else {
                await api.post("/admin/pages", form);
            }
            toast.success(isEdit ? "Updated" : "Created");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/70 p-0 sm:items-center sm:p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="flex max-h-screen w-full max-w-5xl flex-col overflow-hidden border border-border-dim bg-surface sm:max-h-[90vh]">
                <div className="flex items-center justify-between border-b border-border-dim p-5">
                    <h2 className="font-display text-2xl text-white">{isEdit ? "Edit page" : "New page"}</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowPreview((p) => !p)}
                            className="inline-flex items-center gap-2 border border-border-dim px-3 py-2 text-[11px] uppercase tracking-widest text-white/70 hover:border-gold hover:text-gold"
                        >
                            <Eye className="h-3 w-3" /> {showPreview ? "Edit" : "Preview"}
                        </button>
                        <button onClick={onClose} className="text-white/60 hover:text-heart">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {showPreview ? (
                    <div className="flex-1 overflow-y-auto p-8">
                        <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Preview</p>
                        <h1 className="mt-3 font-display text-4xl text-white sm:text-5xl">{form.title}</h1>
                        {form.subtitle && <p className="mt-4 text-white/60">{form.subtitle}</p>}
                        <article className="prose prose-invert mt-8 max-w-none text-white/80">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    h1: ({ ...p }) => <h1 className="font-display text-3xl text-white mt-8 mb-3" {...p} />,
                                    h2: ({ ...p }) => <h2 className="font-display text-2xl text-white mt-6 mb-2" {...p} />,
                                    h3: ({ ...p }) => <h3 className="font-display text-xl text-gold mt-5 mb-2" {...p} />,
                                    a: ({ ...p }) => <a className="text-gold underline" {...p} />,
                                    ul: ({ ...p }) => <ul className="my-3 ml-6 list-disc" {...p} />,
                                    li: ({ ...p }) => <li className="mb-1" {...p} />,
                                    strong: ({ ...p }) => <strong className="text-white" {...p} />,
                                    em: ({ ...p }) => <em className="italic text-gold" {...p} />,
                                }}
                            >
                                {form.body || ""}
                            </ReactMarkdown>
                        </article>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field label="Title">
                                <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="page-title" className={inp} />
                            </Field>
                            <Field label="Slug (URL: /p/<slug>)">
                                <input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="page-slug" className={inp} />
                            </Field>
                            <Field label="Subtitle" span={2}>
                                <input value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={inp} />
                            </Field>
                            <Field label="Body (Markdown supported)" span={2}>
                                <textarea
                                    rows={14}
                                    value={form.body || ""}
                                    onChange={(e) => set("body", e.target.value)}
                                    data-testid="page-body"
                                    className={`${inp} font-mono`}
                                />
                                <p className="mt-2 text-[10px] uppercase tracking-widest text-white/40">
                                    Use # ## ### for headings · **bold** · *italic* · [link](url) · - bullets · markdown images
                                </p>
                            </Field>
                            <Field label="SEO title">
                                <input value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inp} />
                            </Field>
                            <Field label="SEO description">
                                <input value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={inp} />
                            </Field>
                            <Field label="" span={2}>
                                <label className="flex items-center gap-3">
                                    <input type="checkbox" checked={!!form.published} onChange={(e) => set("published", e.target.checked)} className="h-4 w-4" />
                                    <span className="text-sm text-white">Published (visible on storefront)</span>
                                </label>
                            </Field>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-end gap-3 border-t border-border-dim p-5">
                    <button onClick={onClose} className="border border-border-dim px-6 py-3 text-[11px] uppercase tracking-widest text-white/70 hover:text-white">
                        Cancel
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="page-save"
                        className="inline-flex items-center gap-2 bg-gold px-6 py-3 text-[11px] uppercase tracking-widest text-obsidian hover:bg-gold-light disabled:opacity-50"
                    >
                        <Save className="h-3 w-3" /> {saving ? "Saving…" : "Save page"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const inp = "w-full border border-border-dim bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-gold";

function Field({ label, children, span = 1 }) {
    return (
        <div className={span === 2 ? "md:col-span-2" : ""}>
            {label && <label className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</label>}
            <div className={label ? "mt-2" : ""}>{children}</div>
        </div>
    );
}
